/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import { useState, useEffect, useCallback } from "react";
import { formUserSettingsController } from "@Actions/Forms";
import { useDispatch, useSelector } from "react-redux";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { formCategoryObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import { RootState } from "@Reducers/index";
import type { FormUserSettingsControllerResponse } from "@ServerTypes/forms/forms.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * `checkFormUserSettings` answers with an `isUserSettingExists` flag that the shared
 * `formUserSettingsControllerResponse` schema — a bare `{ status, result, count }` envelope —
 * does not model. Reported as a server-schema gap; read through this view type meanwhile.
 */
type CheckFormUserSettingsData = FormUserSettingsControllerResponse & { isUserSettingExists?: boolean };

/** Which destructive action the confirm dialog is currently armed for. */
interface WarningModalState {
  status: boolean;
  source?: string;
}

interface UseFromListProps {
  projectId?: string;
  /** A `formCategories` value or a custom module's `resourceType`. */
  formCategory: string;
  isFocused?: boolean;
  showOnlyGrid?: boolean;
}

function useFromList({ projectId, formCategory, isFocused = true, showOnlyGrid = false }: UseFromListProps) {
  const { t } = useTranslation();
  const [userSettingsExists, setUserSettingsExists] = useState(false);
  const [showWarning, setShowWarning] = useState<WarningModalState>({ status: false });
  const [moduleTxt, setModuleTxt] = useState("");

  const dispatch = useDispatch();

  const customModules = useSelector((state: RootState) => state.customModules) || [];

  const getUserSettingsForFormConfig = useCallback(async () => {
    const checkUserSettingExists = await formUserSettingsController({
      action: "checkFormUserSettings",
      // KNOWN BUG, preserved verbatim: `projectId` is the route param, so `string | undefined`, while
      // `formUserSettingsControllerRequest` requires a branded 24-hex `projectId`. The effect below
      // guards on it, but this callback is also returned from the hook and callers do not — so an
      // unguarded call posts `projectId: undefined` and is rejected by Zod server-side. Asserted
      // (erased at emit) to keep that exact request; the fix is to make the prop required.
      projectId: projectId as ObjectIdString,
      resourceType: formCategory,
    });
    setUserSettingsExists(
      !!(checkUserSettingExists.data as CheckFormUserSettingsData | undefined)?.isUserSettingExists,
    );
  }, [projectId, formCategory]);

  useEffect(() => {
    if (isFocused && !showOnlyGrid && projectId) {
      getUserSettingsForFormConfig();
    }
  }, [formCategory, isFocused, projectId, showOnlyGrid, getUserSettingsForFormConfig]);

  useEffect(() => {
    if (customModules.length) {
      const categoryObj = customModules.find((module) => module.resourceType == formCategory);
      const moduleText = categoryObj?.activeCell || formCategoryObject[formCategory]?.activeCell || "";

      setModuleTxt(moduleText);
    } else {
      setModuleTxt(formCategoryObject[formCategory]?.activeCell ?? "");
    }
  }, [customModules, formCategory]);

  const showWarningModal = (source: string) => {
    setShowWarning({ status: true, source });
  };
  const hideWarningModal = () => {
    setShowWarning({ status: false });
  };

  async function resetConfig() {
    if (userSettingsExists) {
      const res = await formUserSettingsController({
        action: "deleteFormUserSettings",
        // Same unguarded `projectId` as `getUserSettingsForFormConfig` above — this path has no guard
        // at all. Erased at emit.
        projectId: projectId as ObjectIdString,
        resourceType: formCategory,
      });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Config reseted successfully") as TranslatedString,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setUserSettingsExists(false);
        hideWarningModal();
      } else {
        hideWarningModal();
        dispatch(
          showToastNotification({
            // Server-authored message: branded with a cast (erased at emit) rather than routed
            // through `untranslated()`, which would add a call this typing pass must not emit.
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } else {
      dispatch(
        showToastNotification({
          content: t("User settings do not exists") as TranslatedString,
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  }

  function resetConfigClick() {
    if (userSettingsExists) {
      showWarningModal("resetConfig");
    } else {
      dispatch(
        showToastNotification({
          content: t("User settings does not exists") as TranslatedString,
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  }

  return {
    userSettingsExists,
    resetConfig,
    getUserSettingsForFormConfig,
    showWarning,
    setShowWarning,
    showWarningModal,
    hideWarningModal,
    resetConfigClick,
    moduleTxt,
  };
}

export default useFromList;
